import { useUserContext } from "@repo/baseCloud";
import { updateValuesWrapper } from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import {
	applyFieldAuditBusinessLogic,
	copyResponseMap,
	mapListToResponse,
	mapResponseToList,
	toScoreFields,
	useSharedRepFieldAuditsLists,
} from "@repo/field-audits";
import { PushProcessingNotification } from "@repo/helpers";
import { useCallback, useEffect, useMemo } from "react";

import { useTabCountQuery } from "../../../api/repApi";
import { useRepFieldAuditApi, useRepFieldAuditQueries } from "../../../api/repFieldAuditApi";
import { EditableItemDynamicTab } from "../../../components/dynamicEditor/dynamicTab/editableItem";
import { useRepContext, useRepFieldAuditContext } from "../../../contexts/editableItemContext";
import { tabs } from "../../../string";

function useRepFieldAuditsLists() {
	const { useFormTypesQuery, useQuestionOptionsQuery, useComplaintTypesQuery, useLocationsQuery } =
		useRepFieldAuditQueries();
	const { useSettingsQuery } = useRepFieldAuditQueries() as any; // Assuming it might be there or somewhere similar
	const getReps = () => [];

	return useSharedRepFieldAuditsLists({
		useFormTypesQuery,
		useQuestionOptionsQuery,
		useComplaintTypesQuery,
		useLocationsQuery,
		getReps,
		useSettingsQuery: useSettingsQuery || (() => ({ data: {}, loaded: true })),
	});
}

function OnboardingAuditEditorDynamic() {
	const { user } = useUserContext();
	const { rep, repId } = useRepContext();
	const context = useRepFieldAuditContext();
	const [activeLists] = useRepFieldAuditsLists();
	const { getFieldAuditQuestions, saveFieldAudit } = useRepFieldAuditApi();
	const { useQuestionsQuery, useRepFieldAuditQuery } = useRepFieldAuditQueries();
	const { clearQuery: clearFieldAuditQuery } = useRepFieldAuditQuery();
	const { clearQuery: clearTabCount } = useTabCountQuery(repId);
	const repFieldAudit = context.item;
	const { data: fieldAuditQuestions, clearQuery: clearFieldAuditQuestionsQuery } = useQuestionsQuery(
		repFieldAudit?.representativeFieldAuditId,
		repFieldAudit?.formTypeId,
	);

	const templateData = useMemo(
		() => ({
			// The update queue copies only one level before writing answers into responseMap entries, and the item
			// shares responseMap with savedItem (overwriteItem), so give it a copy: otherwise answering mutates the
			// saved audit and Cancel can't revert answers.
			repFieldAudit: repFieldAudit?.responseMap
				? { ...repFieldAudit, responseMap: copyResponseMap(repFieldAudit.responseMap) }
				: repFieldAudit,
			user,
			rep,
			activeLists,
		}),
		[repFieldAudit, user, rep, activeLists],
	);

	useEffect(() => {
		if (fieldAuditQuestions) {
			context.overwriteItem({
				responseMap: {},
				...repFieldAudit,
				...fieldAuditQuestions,
			});
		}
	}, [fieldAuditQuestions]);

	useEffect(() => {
		if (repFieldAudit?.questionGroups?.length > 0) {
			const { responseMap, score, scoreTotal } = mapListToResponse(repFieldAudit, activeLists);
			context.overwriteItem({
				...repFieldAudit,
				...toScoreFields(score, scoreTotal),
				responseMap,
			});
		}
	}, [JSON.stringify(repFieldAudit?.questionGroups)]);

	const save = useCallback(
		async (email: boolean) => {
			const notify = PushProcessingNotification("Saving Field Audit");
			try {
				mapResponseToList(repFieldAudit);
				const id = await saveFieldAudit(repFieldAudit, email);
				notify.processing("Loading...");
				const newFieldAudit = await getFieldAuditQuestions(repId, id, undefined);
				await clearFieldAuditQuery();
				await clearFieldAuditQuestionsQuery();
				await clearTabCount();
				const { responseMap, score, scoreTotal } = mapListToResponse(newFieldAudit, activeLists);
				notify.success("Saved!");
				return {
					...newFieldAudit,
					score: score / scoreTotal,
					scoreAmount: score,
					scoreTotal,
					responseMap,
				};
			} catch (ex) {
				notify.error("Failed to save");
				throw ex;
			}
		},
		[repFieldAudit],
	);

	const applyBusinessLogic = async (id: string, value: any, newState: any) => {
		return applyFieldAuditBusinessLogic(id, value, newState, repFieldAudit);
	};

	const updateValues = useMemo(() => updateValuesWrapper(applyBusinessLogic), [applyBusinessLogic]);

	return (
		<EditableItemDynamicTab
			itemContext={context}
			pageName={tabs.fieldAudits}
			otherTemplateData={templateData}
			itemProp="repFieldAudit"
			updateValues={updateValues}
			save={save}
			loaded
			saveAndEmail
		/>
	);
}

export default OnboardingAuditEditorDynamic;
